import styled from 'styled-components';

import { mixins } from '../../../style/mixins';
import type { BackgroundSize } from './types';
import { CardVariant } from './types';
import { Wrapper as InfoLabel } from './components/InfoLabel/InfoLabel.styled';

export const ContextualMenuWrapper = styled.div<{ $disabled?: boolean }>(
  ({ $disabled }) => ({
    opacity: $disabled ? 0 : undefined,
    pointerEvents: $disabled ? 'none' : undefined,
  }),
);

export const SelectedBorder = styled.div<{ $show?: boolean }>(
  ({ theme: { borderRadius, color, size, transition }, $show }) => ({
    position: 'absolute',
    opacity: $show ? 1 : 0,

    borderRadius: size.units(1.25),
    border: `3px solid ${color.border.accent.default()}`,
    zIndex: 1,
    width: 'calc(100% + 8px)',
    height: 'calc(100% + 8px)',
    top: -4,
    left: -4,
    pointerEvents: 'none',
    transition: transition.standard('opacity'),

    '&::after': {
      content: "''",
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: borderRadius.medium,
      border: `3px solid ${color.border.reversed.default()}`,
    },
  }),
);

export const Overlay = styled.div(({ theme: { borderRadius, color } }) => ({
  width: '100%',
  height: '100%',
  borderRadius: borderRadius.medium,
  transition: 'opacity 0.3s ease-in-out',
  backgroundColor: color.background.reversed.default(0.8),
  opacity: 0,
}));

export const TopRowWrapper = styled.div(({ theme: { size } }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  minHeight: size.units(4.25),
}));

export const BottomLabelWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: size.units(0.625),
}));

export const BottomRowWrapper = styled.div(({ theme: { size } }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: size.units(4.25),

  [BottomLabelWrapper]: {
    zIndex: 0,
  },
}));

export const InfoLabelWrapper = styled.div(({ theme: { size } }) => ({
  cursor: 'default',
  padding: `0 ${size.units(0.625)} ${size.units(0.625)} 0`,
}));

interface TextRoundedButtonsWrapperProps {
  $isSingleButton: boolean;
}

export const TextRoundedButtonsWrapper = styled.div<TextRoundedButtonsWrapperProps>(
  ({ theme: { size }, $isSingleButton }) => ({
    display: 'flex',
    gap: size.units(1.5),
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    cursor: $isSingleButton ? 'pointer' : undefined,
    opacity: 0,
  }),
);

interface ImagePreviewProps {
  $imageUrl?: string;
  $backgroundSize: BackgroundSize;
}

export const ImagePreview = styled.div<ImagePreviewProps>(
  ({ theme: { borderRadius }, $imageUrl, $backgroundSize }) => ({
    position: 'relative',
    flexDirection: 'column',
    backgroundImage: $imageUrl ? `url("${$imageUrl}")` : undefined,
    backgroundSize: $backgroundSize,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    borderRadius: borderRadius.medium,
    width: '100%',
    height: '100%',
  }),
);

export const IconButtonsWrapper = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
  cursor: 'pointer',
  opacity: 0,
});

export const IconButtonsContainer = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  gap: size.units(1.25),
  padding: `${size.units(0.625)} ${size.units(1.25)} ${size.units(0.625)} ${size.units(
    0.625,
  )}`,
}));

export const CheckboxWrapper = styled.div<{ $alwaysShow?: boolean }>(
  ({ theme: { size, transition }, $alwaysShow }) => ({
    padding: `${size.units(0.625)} ${size.units(1.25)} ${size.units(0.625)} ${size.units(
      0.625,
    )}`,

    transition: transition.standard('opacity'),

    opacity: $alwaysShow ? 1 : 0,
  }),
);

export const LabelsWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  gap: size.units(0.625),
  padding: size.units(0.625),
  justifyContent: 'flex-start',
  width: '100%',
  flex: 1,
  cursor: 'default',
}));

export const ContentWrapper = styled.div({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

export const FavoriteButtonWrapper = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
  cursor: 'pointer',
});

export const FavoriteButtonContainer = styled.div(({ theme: { size } }) => ({
  padding: size.units(0.625),
  zIndex: 1,
}));

interface WrapperProps {
  $isClickable: boolean;
  $variant: CardVariant;
  $isFavorite: boolean;
  $boxShadow?: string;
  $disableHover?: boolean;
}

export const CardWrapper = styled.div<WrapperProps>(
  ({
    theme: { shadow, borderRadius, color },
    $variant,
    $isClickable,
    $isFavorite,
    $boxShadow,
    $disableHover,
  }) => ({
    position: 'relative',
    boxShadow: $boxShadow ?? shadow.short,
    borderRadius: borderRadius.medium,
    cursor: $isClickable ? 'pointer' : undefined,
    backgroundColor: color.background.secondary.default(),
    flexGrow: 1,

    [ContentWrapper]: {
      position: 'absolute',
    },

    [LabelsWrapper]: {
      zIndex: 1,
    },

    [TopRowWrapper]: {
      zIndex: 1,
    },

    [TextRoundedButtonsWrapper]: {
      pointerEvents: $variant === CardVariant.SELECTABLE ? 'none' : undefined,
      zIndex: 1,
    },

    [IconButtonsWrapper]: {
      pointerEvents: $variant === CardVariant.SELECTABLE ? 'none' : undefined,
    },

    [Overlay]: {
      position: 'absolute',
      zIndex: 1,
    },

    [FavoriteButtonWrapper]: {
      opacity: $isFavorite ? 1 : 0,
    },

    ...(!$disableHover &&
      mixins.devices.desktop({
        '&:hover, &:focus-visible': {
          boxShadow: shadow.shortDarker,

          [Overlay]: {
            opacity: 1,
          },

          [TextRoundedButtonsWrapper]: {
            opacity: $variant === CardVariant.DEFAULT ? 1 : undefined,
          },

          [IconButtonsWrapper]: {
            opacity: $variant === CardVariant.DEFAULT ? 1 : undefined,
          },

          [CheckboxWrapper]: {
            opacity: $variant === CardVariant.DEFAULT ? 1 : undefined,
          },

          [FavoriteButtonWrapper]: {
            opacity: 1,
          },

          [InfoLabel]: {
            maxWidth: '100%',
          },
        },

        // we need to find an alternative to :has for firefox in the next iteration
        '&:has(:focus-visible)': {
          boxShadow: shadow.shortDarker,

          [Overlay]: {
            opacity: 1,
          },

          [TextRoundedButtonsWrapper]: {
            opacity: 1,
          },

          [IconButtonsWrapper]: {
            opacity: 1,
          },

          [FavoriteButtonWrapper]: {
            opacity: 1,
          },
        },
      })),
  }),
);

export const Footer = styled.div(({ theme: { size } }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'space-between',
  paddingTop: size.units(1),
  gap: size.units(1),
}));

export const Wrapper = styled.div<{ $disabled?: boolean; $avoidSelection?: boolean }>(
  ({ theme: { transition }, $disabled, $avoidSelection }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: transition.standard('opacity'),
    opacity: $disabled ? 0.5 : 1,
    pointerEvents: $disabled ? 'none' : 'auto',
    userSelect: $avoidSelection ? 'none' : undefined,
  }),
);
